import { useCallback } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { DataApi } from '@eis/dataservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import type { EisApiClients } from './clients';
import { refusalReasonOf } from './gate';
import { apiKeys } from './keys';
import { isApiError, unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';
import type { Job } from './jobs';

/**
 * THE DATA SUITE's data layer (SDD-024 §6, §8) — reports, custom datasets, the grouped catalog,
 * the workspace settings and the warehouse queries, read and written through `@eis/dataservice-sdk`
 * and nothing else (T-7).
 *
 * Three facts shape every hook here:
 *
 *  - No request waits for the warehouse (Q-1): `:run`, `:preview`, `:sample` and `:fieldValues`
 *    answer a `Query` at once, which {@link useReportingQuery} polls — every second, backing off to
 *    three after thirty seconds, never once it is final.
 *  - Heads and the settings are versioned (T-12): every write quotes the head's ETag explicitly
 *    (`If-Match`) and its `expectedVersion`, from the head the editor holds — the editor autosaves,
 *    so the "last read" the SDK parked may be older than the head the screen shows.
 *  - Documents (`report.schema.json`, `dataset.schema.json`) travel as plain JSON objects and are
 *    validated by the service; a refusal names its code (`DOCUMENT_INVALID`, `FIELD_UNKNOWN`, …)
 *    as the ErrorInfo reason ({@link reportingReasonOf}).
 */
const V1 = '/dataservice/v1';

export type Report = DataApi.Report;
export type CustomDataset = DataApi.CustomDataset;
export type ReportQuery = DataApi.Query;
export type QueryColumn = DataApi.QueryColumn;
export type CatalogDataset = DataApi.CatalogDataset;
export type DatasetField = DataApi.DatasetField;
export type DocumentVersion = DataApi.DocumentVersion;
export type WorkspaceSettings = DataApi.WorkspaceSettings;
export type ReportFilterValueWire = DataApi.FilterValue;
/** A report or dataset document as JSON (validated by the service, not here). */
export type DocumentJson = Record<string, unknown>;

/** The refusal codes of the Data Suite (SDD-024 §6.3) plus the list standard's (SDD-017). */
export const REPORTING_CODES = [
  'DOCUMENT_INVALID',
  'FIELD_UNKNOWN',
  'DATASET_UNKNOWN',
  'DATASET_VERSION_NOT_PUBLISHED',
  'DATASET_IN_USE',
  'PUBLISH_NEEDS_RUN',
  'NOT_AUTHOR',
  'PRIVATE_REPORT',
  'WAREHOUSE_NOT_CONFIGURED',
  'WAREHOUSE_UNAVAILABLE',
  'QUERY_FAILED',
  'QUERY_CANCELLED',
  'QUERY_TIMEOUT',
  'TOO_MANY_GROUPS',
  'EXPRESSION_FORBIDDEN',
  'EXPRESSION_INVALID',
  'EXPRESSION_TYPE_MISMATCH',
  'JOIN_TYPE_MISMATCH',
  'EXPORT_TOO_LARGE',
  'CATALOG_REFRESHING',
  'INVALID_FILTER',
  'INVALID_SEARCH',
  'INVALID_ORDER',
  'INVALID_PAGE_TOKEN',
  'INVALID_EXPAND',
  'INVALID_ARGUMENT',
] as const;
export type ReportingCode = (typeof REPORTING_CODES)[number];

/** The refusal code of a failed call (ErrorInfo reason), if the service named one. */
export function reportingReasonOf(error: unknown): string | undefined {
  return refusalReasonOf(error);
}

/** 403 PRIVATE_REPORT — another user's private report (R-3, R-7). */
export function isPrivateReport(error: unknown): boolean {
  return (
    reportingReasonOf(error) === 'PRIVATE_REPORT' || (isApiError(error) && error.status === 403)
  );
}

/** The field-level violations of a DOCUMENT_INVALID refusal (google.rpc.BadRequest). */
export function documentViolationsOf(error: unknown): { field: string; description: string }[] {
  if (!isApiError(error)) return [];
  const body = error.body as { details?: unknown } | undefined;
  const details = Array.isArray(body?.details) ? (body.details as unknown[]) : [];
  for (const detail of details) {
    if (typeof detail !== 'object' || detail === null) continue;
    const type = (detail as { '@type'?: unknown })['@type'];
    if (typeof type !== 'string' || !type.endsWith('google.rpc.BadRequest')) continue;
    const violations = (detail as { fieldViolations?: unknown }).fieldViolations;
    if (!Array.isArray(violations)) continue;
    return violations.flatMap((v: unknown) => {
      if (typeof v !== 'object' || v === null) return [];
      const field = (v as { field?: unknown }).field;
      const description = (v as { description?: unknown }).description;
      return [
        {
          field: typeof field === 'string' ? field : '',
          description: typeof description === 'string' ? description : '',
        },
      ];
    });
  }
  return [];
}

// ---------------------------------------------------------------------------------------------
// Queries (Q-1…Q-8)
// ---------------------------------------------------------------------------------------------

const FINAL_QUERY_STATES: readonly string[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

export function isFinalQuery(query: Pick<ReportQuery, 'state'> | undefined): boolean {
  return query?.state !== undefined && FINAL_QUERY_STATES.includes(query.state);
}

/** Q-1: every second, three after thirty seconds of running. Pure, so the backoff is a unit test. */
export function queryPollInterval(elapsedMs: number): number {
  return elapsedMs < 30_000 ? 1000 : 3000;
}

/**
 * One query, polled until it is final (Q-1). `startedAt` is when the console started it — the
 * backoff is measured from there. `id` undefined reads nothing.
 */
export function useReportingQuery(
  id: string | undefined,
  startedAt = 0,
): UseQueryResult<ReportQuery> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.query(id ?? ''),
    queryFn: async () => {
      const data = await unwrap(
        clients.data.client.GET(`${V1}/queries/{id}`, { params: { path: { id: id ?? '' } } }),
      );
      return data.query ?? {};
    },
    enabled: id !== undefined && id !== '',
    refetchInterval: (query) =>
      isFinalQuery(query.state.data) ? false : queryPollInterval(Date.now() - startedAt),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** The cells of a page: strings or null. Accepts the protobuf JSON array and the `{values}` form. */
export function rowsOf(rows: readonly unknown[] | undefined): (string | null)[][] {
  return (rows ?? []).map((row) => {
    const cells: unknown = Array.isArray(row) ? row : (row as { values?: unknown } | null)?.values;
    return Array.isArray(cells)
      ? cells.map((cell: unknown) =>
          typeof cell === 'string'
            ? cell
            : typeof cell === 'number' || typeof cell === 'boolean'
              ? String(cell)
              : null,
        )
      : [];
  });
}

/** Reads the next page of a query by its token (Q-3). */
export function useQueryRowsLoader(): (
  id: string,
  pageToken: string,
) => Promise<{ rows: (string | null)[][]; nextPageToken: string }> {
  const clients = useApiClients();
  return useCallback(
    async (id: string, pageToken: string) => {
      const data = await unwrap(
        clients.data.client.GET(`${V1}/queries/{id}/rows`, {
          params: { path: { id }, query: { pageToken } },
        }),
      );
      return { rows: rowsOf(data.rows), nextPageToken: data.nextPageToken ?? '' };
    },
    [clients],
  );
}

export function useCancelQuery(): ServiceMutation<{ readonly id: string }, ReportQuery> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ id }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/queries/{id}:cancel`, {
          params: { path: { id } },
          body: {},
        }),
      );
      return data.query ?? {};
    },
    invalidates: ({ id }) => [apiKeys.reporting.query(id)],
  });
}

// ---------------------------------------------------------------------------------------------
// Reports (R-1…R-8)
// ---------------------------------------------------------------------------------------------

/** If-Match for a head the screen holds: its ETag, and the version as `expectedVersion`. */
function guard(etag: string | undefined): { headers: Record<string, string> } {
  return etag === undefined || etag === '' ? { headers: {} } : { headers: { 'If-Match': etag } };
}

export function useReport(code: string | undefined): UseQueryResult<Report> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.report(code ?? ''),
    queryFn: async () => {
      const data = await unwrap(
        clients.data.client.GET(`${V1}/reports/{code}`, { params: { path: { code: code ?? '' } } }),
      );
      return data.report ?? {};
    },
    enabled: code !== undefined && code !== '',
    retry: (count, error) => !(isApiError(error) && error.status < 500) && count < 2,
  });
}

export function useReportVersions(code: string | undefined): UseQueryResult<DocumentVersion[]> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.reportVersions(code ?? ''),
    queryFn: async () => {
      const data = await unwrap(
        clients.data.client.GET(`${V1}/reports/{code}/versions`, {
          params: { path: { code: code ?? '' } },
        }),
      );
      return data.items ?? [];
    },
    enabled: code !== undefined && code !== '',
  });
}

/** Reads one published version with its document — "Als Entwurf laden" in the editor's history. */
export function useFetchReportVersion(): (
  code: string,
  number: number,
) => Promise<DocumentVersion> {
  const clients = useApiClients();
  return useCallback(
    async (code, number) => {
      const data = await unwrap(
        clients.data.client.GET(`${V1}/reports/{code}/versions/{number}`, {
          params: { path: { code, number } },
        }),
      );
      return data.version ?? {};
    },
    [clients],
  );
}

const reportWrites = (code: string) => [
  apiKeys.reporting.report(code),
  apiKeys.reporting.reportList(),
];

export function useCreateReport(): ServiceMutation<{ readonly document: DocumentJson }, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ document }) => {
      const data = await unwrap(clients.data.client.POST(`${V1}/reports`, { body: { document } }));
      return data.report ?? {};
    },
    invalidates: () => [apiKeys.reporting.reportList()],
  });
}

export interface PutDraftVariables {
  readonly code: string;
  readonly document: DocumentJson;
  /** The head's ETag and version the editor holds. */
  readonly etag: string | undefined;
  readonly version: string | undefined;
}

export function usePutReportDraft(): ServiceMutation<PutDraftVariables, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, document, etag, version }) => {
      const data = await unwrap(
        clients.data.client.PUT(`${V1}/reports/{code}/draft`, {
          params: { path: { code } },
          body: { document, ...(version === undefined ? {} : { expectedVersion: version }) },
          ...guard(etag),
        }),
      );
      return data.report ?? {};
    },
    // The editor keeps the answer as its head; re-reading on every autosave would race it.
    invalidates: () => [apiKeys.reporting.reportList()],
  });
}

export interface HeadVariables {
  readonly code: string;
  readonly etag: string | undefined;
  readonly version: string | undefined;
}

export function usePublishReportDraft(): ServiceMutation<HeadVariables, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/reports/{code}/draft:publish`, {
          params: { path: { code } },
          body: version === undefined ? {} : { expectedVersion: version },
          ...guard(etag),
        }),
      );
      return data.report ?? {};
    },
    invalidates: ({ code }) => [...reportWrites(code), apiKeys.reporting.reportVersions(code)],
  });
}

export function useDiscardReportDraft(): ServiceMutation<HeadVariables, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version }) => {
      const data = await unwrap(
        clients.data.client.DELETE(`${V1}/reports/{code}/draft`, {
          params: {
            path: { code },
            query: version === undefined ? {} : { expectedVersion: version },
          },
          ...guard(etag),
        }),
      );
      return data.report ?? {};
    },
    invalidates: ({ code }) => reportWrites(code),
  });
}

export interface UpdateReportVariables extends HeadVariables {
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: 'PRIVATE' | 'WORKSPACE';
}

export function useUpdateReport(): ServiceMutation<UpdateReportVariables, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version, name, description, visibility }) => {
      const data = await unwrap(
        clients.data.client.PATCH(`${V1}/reports/{code}`, {
          params: { path: { code } },
          body: {
            ...(name === undefined ? {} : { name }),
            ...(description === undefined ? {} : { description }),
            ...(visibility === undefined ? {} : { visibility }),
            ...(version === undefined ? {} : { expectedVersion: version }),
          },
          ...guard(etag),
        }),
      );
      return data.report ?? {};
    },
    invalidates: ({ code }) => reportWrites(code),
  });
}

export function useDuplicateReport(): ServiceMutation<
  { readonly code: string; readonly version?: number; readonly name?: string },
  Report
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, version, name }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/reports/{code}:duplicate`, {
          params: { path: { code } },
          body: {
            ...(version === undefined ? {} : { version }),
            ...(name === undefined ? {} : { name }),
          },
        }),
      );
      return data.report ?? {};
    },
    invalidates: () => [apiKeys.reporting.reportList()],
  });
}

export function useDeleteReport(): ServiceMutation<HeadVariables, Report> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version }) => {
      const data = await unwrap(
        clients.data.client.DELETE(`${V1}/reports/{code}`, {
          params: {
            path: { code },
            query: version === undefined ? {} : { expectedVersion: version },
          },
          ...guard(etag),
        }),
      );
      return data.report ?? {};
    },
    invalidates: ({ code }) => reportWrites(code),
  });
}

/** The viewer's sort (`SortKey`): replaces the report's for one run or export. */
export interface ReportSortKey {
  readonly by: string;
  readonly direction: 'ASC' | 'DESC';
}

export interface RunReportVariables {
  readonly code: string;
  readonly version?: number;
  readonly filterValues?: readonly ReportFilterValueWire[];
  readonly bypassReuse?: boolean;
  readonly sort?: readonly ReportSortKey[];
}

export function useRunReport(): ServiceMutation<RunReportVariables, ReportQuery> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, version, filterValues, bypassReuse, sort }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/reports/{code}:run`, {
          params: { path: { code } },
          body: {
            ...(version === undefined ? {} : { version }),
            filterValues: [...(filterValues ?? [])],
            ...(bypassReuse === true ? { bypassReuse: true } : {}),
            ...(sort === undefined || sort.length === 0 ? {} : { sort: [...sort] }),
          },
        }),
      );
      return data.query ?? {};
    },
    invalidates: () => [],
  });
}

export interface PreviewReportVariables {
  readonly code: string;
  readonly document: DocumentJson;
  readonly filterValues?: readonly ReportFilterValueWire[];
  readonly limit?: number;
}

export function usePreviewReport(): ServiceMutation<PreviewReportVariables, ReportQuery> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, document, filterValues, limit }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/reports/{code}:preview`, {
          params: { path: { code } },
          body: {
            document,
            filterValues: [...(filterValues ?? [])],
            ...(limit === undefined ? {} : { limit }),
          },
        }),
      );
      return data.query ?? {};
    },
    // A successful preview of the draft makes it publishable (R-5): the head re-reads later.
    invalidates: () => [],
  });
}

export interface ExportReportVariables {
  readonly code: string;
  readonly format: 'CSV' | 'XLSX';
  readonly version?: number;
  readonly filterValues?: readonly ReportFilterValueWire[];
  readonly label: string;
  readonly sort?: readonly ReportSortKey[];
}

/** E-1: a report export is a job of the Data Suite's service, shown in the jobs tray. */
export function useExportReport(): ServiceMutation<ExportReportVariables, Job> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, format, version, filterValues, label, sort }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/reports/{code}:export`, {
          params: { path: { code } },
          body: {
            format,
            label,
            ...(version === undefined ? {} : { version }),
            filterValues: [...(filterValues ?? [])],
            ...(sort === undefined || sort.length === 0 ? {} : { sort: [...sort] }),
          },
        }),
      );
      return data.job as Job;
    },
    invalidates: () => [apiKeys.jobs.mine('data')],
  });
}

export interface ValidateExpressionVariables {
  readonly dataset?: DocumentJson;
  readonly datasetDocument?: DocumentJson;
  readonly expression: string;
  readonly type: string;
  readonly calculatedFields?: readonly DocumentJson[];
}

/** X-3: the dry-run answers the outcome; only a transport failure throws. */
export function useValidateExpression(): ServiceMutation<
  ValidateExpressionVariables,
  DataApi.ValidateExpressionResponse
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ dataset, datasetDocument, expression, type, calculatedFields }) =>
      unwrap(
        clients.data.client.POST(`${V1}/expressions:validate`, {
          body: {
            ...(dataset === undefined ? {} : { dataset }),
            ...(datasetDocument === undefined ? {} : { datasetDocument }),
            expression,
            type,
            ...(calculatedFields === undefined
              ? {}
              : { calculatedFields: [...calculatedFields] as unknown as DataApi.ListValue }),
          },
        }),
      ),
    invalidates: () => [],
  });
}

// ---------------------------------------------------------------------------------------------
// Catalog (W-3…W-6) and custom datasets (S-1…S-6)
// ---------------------------------------------------------------------------------------------

export function useDatasets(): UseQueryResult<DataApi.ListDatasetsResponse> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.catalog(),
    queryFn: () => unwrap(clients.data.client.GET(`${V1}/datasets`)),
    // While a refresh runs the snapshot will change: read again every few seconds.
    refetchInterval: (query) => (query.state.data?.refreshing === true ? 3000 : false),
  });
}

export function useDataset(name: string | undefined): UseQueryResult<DataApi.GetDatasetResponse> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.dataset(name ?? ''),
    queryFn: () =>
      unwrap(
        clients.data.client.GET(`${V1}/datasets/{name}`, {
          params: { path: { name: name ?? '' } },
        }),
      ),
    enabled: name !== undefined && name !== '',
    staleTime: 5 * 60_000,
  });
}

/** A catalog dataset's first rows: 5 by default ("Beispielwerte"), up to 100 (a source's preview). */
export function useSampleDataset(): ServiceMutation<
  { readonly name: string; readonly limit?: number },
  ReportQuery
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ name, limit }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/datasets/{name}:sample`, {
          params: { path: { name } },
          body: limit === undefined ? {} : { limit },
        }),
      );
      return data.query ?? {};
    },
    invalidates: () => [],
  });
}

export function useRefreshCatalog(): ServiceMutation<Record<string, never>, Job> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async () => {
      const data = await unwrap(clients.data.client.POST(`${V1}/catalog:refresh`, { body: {} }));
      return data.job as Job;
    },
    invalidates: () => [apiKeys.reporting.catalog(), apiKeys.jobs.mine('data')],
  });
}

async function readQuery(clients: EisApiClients, id: string): Promise<ReportQuery> {
  const data = await unwrap(
    clients.data.client.GET(`${V1}/queries/{id}`, { params: { path: { id } } }),
  );
  return data.query ?? {};
}

/**
 * Value suggestions for a STRING field (SQL-6): the lookup is a query like every other; the loader
 * polls it (at most ten seconds) and answers the values, most frequent first.
 */
export function useDatasetFieldValues(
  datasetName: string | undefined,
): (field: string, prefix: string) => Promise<readonly string[]> {
  const clients = useApiClients();
  return useCallback(
    async (field: string, prefix: string) => {
      if (datasetName === undefined || datasetName === '') return [];
      const started = await unwrap(
        clients.data.client.GET(`${V1}/datasets/{name}:fieldValues`, {
          params: { path: { name: datasetName }, query: { field, prefix } },
        }),
      );
      let query = started.query ?? {};
      for (let i = 0; i < 20 && !isFinalQuery(query) && query.id !== undefined; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        query = await readQuery(clients, query.id);
      }
      if (query.state !== 'SUCCEEDED') return [];
      return rowsOf(query.rows as unknown[] | undefined).flatMap((row) =>
        row[0] == null ? [] : [row[0]],
      );
    },
    [clients, datasetName],
  );
}

export function useCustomDataset(
  code: string | undefined,
): UseQueryResult<DataApi.GetCustomDatasetResponse> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.customDataset(code ?? ''),
    queryFn: () =>
      unwrap(
        clients.data.client.GET(`${V1}/custom-datasets/{code}`, {
          params: { path: { code: code ?? '' } },
        }),
      ),
    enabled: code !== undefined && code !== '',
  });
}

/** A dataset a report runs on, as the report document names it. */
export interface ReportDatasetRef {
  readonly kind: 'CATALOG' | 'CUSTOM';
  readonly name?: string;
  readonly code?: string;
}

/**
 * The field names of a dataset, read (or taken from the cache) when a new report starts on it: the
 * report schema wants at least one value (pivot, chart) or one column (table), so a new report
 * starts with the dataset's first field instead of an empty shelf the service would refuse.
 */
export function useFetchDatasetFields(): (dataset: ReportDatasetRef) => Promise<string[]> {
  const clients = useApiClients();
  const queryClient = useQueryClient();
  return useCallback(
    async (dataset) => {
      const fields =
        dataset.kind === 'CUSTOM'
          ? (
              await queryClient.query({
                queryKey: apiKeys.reporting.customDataset(dataset.code ?? ''),
                queryFn: () =>
                  unwrap(
                    clients.data.client.GET(`${V1}/custom-datasets/{code}`, {
                      params: { path: { code: dataset.code ?? '' } },
                    }),
                  ),
              })
            ).fields
          : (
              await queryClient.query({
                queryKey: apiKeys.reporting.dataset(dataset.name ?? ''),
                queryFn: () =>
                  unwrap(
                    clients.data.client.GET(`${V1}/datasets/{name}`, {
                      params: { path: { name: dataset.name ?? '' } },
                    }),
                  ),
                staleTime: 5 * 60_000,
              })
            ).fields;
      return (fields ?? []).map((f) => f.name ?? '').filter((n) => n !== '');
    },
    [clients, queryClient],
  );
}

const datasetWrites = (code: string) => [
  apiKeys.reporting.customDataset(code),
  apiKeys.reporting.customDatasetList(),
  apiKeys.reporting.catalog(),
];

export function useCreateCustomDataset(): ServiceMutation<
  { readonly document: DocumentJson },
  CustomDataset
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ document }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/custom-datasets`, { body: { document } }),
      );
      return data.dataset ?? {};
    },
    invalidates: () => [apiKeys.reporting.customDatasetList(), apiKeys.reporting.catalog()],
  });
}

export function usePutCustomDatasetDraft(): ServiceMutation<PutDraftVariables, CustomDataset> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, document, etag, version }) => {
      const data = await unwrap(
        clients.data.client.PUT(`${V1}/custom-datasets/{code}/draft`, {
          params: { path: { code } },
          body: { document, ...(version === undefined ? {} : { expectedVersion: version }) },
          ...guard(etag),
        }),
      );
      return data.dataset ?? {};
    },
    invalidates: () => [apiKeys.reporting.customDatasetList()],
  });
}

export function usePublishCustomDatasetDraft(): ServiceMutation<HeadVariables, CustomDataset> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/custom-datasets/{code}/draft:publish`, {
          params: { path: { code } },
          body: version === undefined ? {} : { expectedVersion: version },
          ...guard(etag),
        }),
      );
      return data.dataset ?? {};
    },
    invalidates: ({ code }) => datasetWrites(code),
  });
}

export function usePreviewCustomDataset(): ServiceMutation<
  { readonly code: string; readonly document: DocumentJson },
  ReportQuery
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, document }) => {
      const data = await unwrap(
        clients.data.client.POST(`${V1}/custom-datasets/{code}/draft:preview`, {
          params: { path: { code } },
          body: { document },
        }),
      );
      return data.query ?? {};
    },
    invalidates: () => [],
  });
}

export function useDeleteCustomDataset(): ServiceMutation<HeadVariables, CustomDataset> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ code, etag, version }) => {
      const data = await unwrap(
        clients.data.client.DELETE(`${V1}/custom-datasets/{code}`, {
          params: {
            path: { code },
            query: version === undefined ? {} : { expectedVersion: version },
          },
          ...guard(etag),
        }),
      );
      return data.dataset ?? {};
    },
    invalidates: ({ code }) => datasetWrites(code),
  });
}

// ---------------------------------------------------------------------------------------------
// Workspace settings (F-1, Q-4)
// ---------------------------------------------------------------------------------------------

export function useWorkspaceSettings(): UseQueryResult<WorkspaceSettings> {
  const clients = useApiClients();
  return useQuery({
    queryKey: apiKeys.reporting.settings(),
    queryFn: async () => {
      const data = await unwrap(clients.data.client.GET(`${V1}/workspace-settings`));
      return data.settings ?? {};
    },
    staleTime: 5 * 60_000,
  });
}

export function useUpdateWorkspaceSettings(): ServiceMutation<
  {
    readonly document: DocumentJson;
    readonly etag: string | undefined;
    readonly version: string | undefined;
  },
  WorkspaceSettings
> {
  const clients = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ document, etag, version }) => {
      const data = await unwrap(
        clients.data.client.PUT(`${V1}/workspace-settings`, {
          body: { document, ...(version === undefined ? {} : { expectedVersion: version }) },
          ...guard(etag),
        }),
      );
      return data.settings ?? {};
    },
    invalidates: () => [apiKeys.reporting.settings()],
  });
}
